import React from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { highlightPlugin } from '@react-pdf-viewer/highlight';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { zoomPlugin } from '@react-pdf-viewer/zoom';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/highlight/lib/styles/index.css';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import type { CSSObject } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import { Close } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { PDF_WORKER_URL } from '../../../core-utils/helper-utils/pdfWorker';

/************** SAMPLE PROPS ******************/
//  <PdfHighlighter
//    pdfUrl={selectedDoc?.document_url} ----convert the original url to blob and send
//    chunks={chunks}
//    highlightIds={[
//      'fb8dfa4e-f715-4dd7-bfdf-c168c347d392', ---- chunk id from chunks array
//      'ca0158d1-6b3e-4b87-842f-e7afe1b8d130',
//     ]}
//    pageNumber={4}
//  />

/*************SAMPLE CHUNKS ARRAY **************/
// const chunks = [
//   {
//     grounding: [
//       {
//         page: 0,
//         box: {
//           l: 0.07396268844604492,
//           t: 0.2075837254524231,
//           r: 0.8957719206809998,
//           b: 0.27364060282707214,
//         },
//         label: 'Reference 2',
//       },
//     ],
//     chunk_type: 'text',
//     chunk_id: 'fb8dfa4e-f715-4dd7-bfdf-c168c347d392',
//   },
//   {
//     grounding: [
//       {
//         page: 3,
//         box: {
//           l: 0.5260715484619141,
//           t: 0.944270670413971,
//           r: 0.7958709001541138,
//           b: 0.9849405884742737,
//         },
//         label: 'Reference 4',
//       },
//     ],
//     chunk_type: 'figure',
//     chunk_id: '9aaf4878-e92e-4a7d-b059-d1ed43a4a32e',
//   },
//   {
//     text: '4',
//     grounding: [
//       {
//         page: 3,
//         box: {
//           l: 0.07782295346260071,
//           t: 0.9694303274154663,
//           r: 0.09402281045913696,
//           b: 0.982272744178772,
//         },
//         label: 'Reference 5',
//       },
//     ],
//     chunk_type: 'marginalia',
//     chunk_id: '224d579b-56f0-4e8e-9b14-0d3a07129a73',
//   },
//   {
//     grounding: [
//       {
//         page: 3,
//         box: {
//           l: 0.07022395730018616,
//           t: 0.4556879699230194,
//           r: 0.869985818862915,
//           b: 0.6030611991882324,
//         },
//         label: 'Reference 6',
//       },
//     ],
//     chunk_type: 'table',
//     chunk_id: '5da36798-db7c-4fb8-9cd3-19072deae82e',
//   },
// ];

type Chunk = {
  chunk_id: string;
  grounding: {
    extractions: any;
    page: number;
    box: { left: number; top: number; right: number; bottom: number };
    label?: string;
  }[];
};
export interface PdfHighlighterProps {
  pdfUrl: string;
  chunks: Chunk[];
  highlightIds: string[];
  pageNumber: number;
  highlightColor?: string;
  highlightTextColor?: string;
  contentStyles?: CSSObject;
  onClose?: () => void;
}

const PdfHighlighter: React.FC<PdfHighlighterProps> = ({
  pdfUrl,
  chunks,
  highlightIds,
  pageNumber,
  highlightColor,
  highlightTextColor,
  contentStyles,
  onClose,
}) => {
  const useStyles = makeStyles()({
    contentGrid: {
      height: '750px',
      overflow: 'auto',
      position: 'relative',
      ...contentStyles,
      '@global': {
        '.rpv-core__inner-pages::-webkit-scrollbar': {
          width: '5px',
          height: '5px',
          borderRadius: '12px',
          backgroundColor: patientAccessTheme.palette.primary.light,
        },
        '.rpv-core__inner-pages::-webkit-scrollbar-track': {
          background: patientAccessTheme.palette.grey[300],
          borderRadius: '12px',
        },
        '.rpv-core__inner-pages::-webkit-scrollbar-thumb': {
          background: patientAccessTheme.palette.grey[500],
          borderRadius: '12px',
        },
      },
    },
    closeIcon: {
      position: 'absolute',
      zIndex: 10,
    },
  });
  const { classes } = useStyles();

  // Highlight plugin
  const highlightPluginInstance = highlightPlugin({
    renderHighlights: ({ pageIndex }) => {
      return (
        <>
          {chunks
            .filter((c) => highlightIds.includes(c.chunk_id))
            .flatMap((c) => {
              const groundingArr = Array.isArray(c.grounding)
                ? c.grounding
                : [c.grounding];
              return groundingArr.filter((g) => g?.page === pageIndex);
            })
            .map((g, i) => {
              const { left, top, right, bottom } = g.box;

              const leftBox = left * 100;
              const topBox = top * 100;
              const width = (right - left) * 100;
              const height = (bottom - top) * 100;

              return (
                <React.Fragment key={i}>
                  {/* Blue border highlight */}
                  <div
                    style={{
                      position: 'absolute',
                      left: `${leftBox}%`,
                      top: `${topBox}%`,
                      width: `${width}%`,
                      height: `${height}%`,
                      border: `2px solid ${
                        highlightColor ??
                        patientAccessTheme.palette.primary[500]
                      }`,
                      boxSizing: 'border-box',
                      pointerEvents: 'none',
                    }}
                  />

                  {/* Label above the box */}
                  {(g?.extractions?.reference || g?.label) && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${leftBox}%`,
                        top: `${topBox - 2.5}%`, // shift label slightly above
                        backgroundColor:
                          highlightColor ??
                          patientAccessTheme.palette.primary[500],
                        color: highlightTextColor ?? 'white',
                        fontSize: '10px',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                      }}
                    >
                      {g?.extractions?.reference !== ''
                        ? g?.extractions?.reference
                        : g?.label ?? ``}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
        </>
      );
    },
  });

  // Page navigation plugin
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { jumpToPage } = pageNavigationPluginInstance;

  //Zoom Plugin
  const zoomPluginInstance = zoomPlugin();
  const { zoomTo } = zoomPluginInstance;

  // 🔹 Navigate once the document is loaded
  const handleDocumentLoad = () => {
    if (pageNumber !== undefined) {
      jumpToPage(pageNumber); // jumpToPage works on 0 based
    }
    zoomTo(1.5);
  };

  return (
    <Worker workerUrl={PDF_WORKER_URL}>
      <div className={classes.contentGrid}>
        {onClose && (
          <div className={classes.closeIcon}>
            <Close
              height={'24px'}
              width={'24px'}
              onClick={onClose}
              cursor={'pointer'}
            />
          </div>
        )}
        <Viewer
          fileUrl={pdfUrl}
          plugins={[highlightPluginInstance, pageNavigationPluginInstance]}
          onDocumentLoad={handleDocumentLoad}
          renderPage={(props) => (
            <>
              {props.canvasLayer.children}
              {props.textLayer.children}
              {/* remove annotationLayer */}
            </>
          )}
        />
      </div>
    </Worker>
  );
};

export default PdfHighlighter;
